import { ChangeEvent, useEffect, useState, FocusEvent, Dispatch, useCallback, MutableRefObject } from 'react';
import debounce from 'lodash.debounce';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';
import { FormApi } from 'final-form';
import { FormSpy } from 'react-final-form';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmListSetFindData } from 'KnmListPackage/MainModule/actions/knmListActions';

import { getErvkOrganisations, setErvkOrganisationsSearchQuery } from '../actions/ChangeKnoInPlanActions';
import { IKnoOrganisationChild } from '../../APIPackage/CatalogsModule/interfaces/IKnoOrganisationChild';
import { getKnoErvkOrganisations } from '../selectors/changeKnoInPlanSelectors';
import { IOrganisationAutocompleteOption } from '../../PersonalAccountPackage/AdminModule/interfaces/IOrganisationAutocompleteOption';
import { IChangeKnoInPlanForm } from '../../PersonalAccountPackage/AdminModule/interfaces/IChangeKnoInPlanForm';
import { REQUIRED_ERROR } from '../../CorePackage/MainModule/constants/validationErrors';
import { TextInputField } from '../../UIPackage/MainModule/components/TextInputField';
import { makeRequired } from '../../CorePackage/MainModule/validators/isRequired';
import { getNotices } from '../../CorePackage/NotificationModule/reducers/noticeReducer';
import { INotification } from '../../CorePackage/NotificationModule/interfaces/INotification';

const SEARCH_TEXT_MIN_LENGTH = 5;
const generateInfoText = (knm: IKnoOrganisationChild) => `${knm.fullName ?? ''}`;

interface IErvkKnoOrganisationSearchProps {
  formRef: MutableRefObject<FormApi<IChangeKnoInPlanForm, Partial<IChangeKnoInPlanForm>> | undefined>;
}

interface IErvkKnoOrganisationSearchStoreProps {
  ervkKnoOrganisations: IKnoOrganisationChild[];
  notifications: INotification[];
}

const ErvkKnoOrganisationSearchWithoutStore = ({
  formRef,
  ervkKnoOrganisations,
  notifications,
}: IErvkKnoOrganisationSearchProps & IErvkKnoOrganisationSearchStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [organisationAutocompleteOption, setOrganisationAutocompleteOption] = useState<
    IOrganisationAutocompleteOption[] | undefined
  >();

  const [isSearchTextSelected, setIsSearchTextSelected] = useState(false);
  const [isFounded, setIsFounded] = useState(false);

  function findErvkOrganisations(searchText?: string) {
    if (searchText && searchText.length >= 5) {
      setIsFounded(true);
      dispatch(setErvkOrganisationsSearchQuery(searchText));
      dispatch(getErvkOrganisations());
    }
  }

  const debouncedFindErvkOrganisations = debounce(findErvkOrganisations, 2000);

  const handleTextInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const valueEdited = value?.trim();

      formRef?.current?.change('ervkKnoOrganizationTitle', valueEdited);
      formRef?.current?.change('ervkKnoOrganization');
      if (!valueEdited) {
        setIsSearchTextSelected(false);
      }
      debouncedFindErvkOrganisations(valueEdited);
      if (!valueEdited || valueEdited.length <= SEARCH_TEXT_MIN_LENGTH) {
        setOrganisationAutocompleteOption(undefined);
      }
    },
    [debouncedFindErvkOrganisations],
  );

  const handleAutoCompleteSelect = useCallback(
    (value: string) => () => {
      const currentOption = organisationAutocompleteOption?.find(item => item.value === value);

      formRef?.current?.change('ervkKnoOrganizationTitle', value);
      formRef?.current?.change('ervkKnoOrganization', currentOption?.ervkKnoOrganization);
      debouncedFindErvkOrganisations('');
      setOrganisationAutocompleteOption(undefined);
      setIsSearchTextSelected(true);
    },
    [debouncedFindErvkOrganisations],
  );

  useEffect(() => {
    isFounded &&
      setOrganisationAutocompleteOption(
        ervkKnoOrganisations.map(kno => {
          const knoText = `${generateInfoText(kno)}`;

          return {
            value: knoText,
            label: knoText,
            ervkKnoOrganization: kno,
          };
        }),
      );
  }, [ervkKnoOrganisations, isFounded]);

  useUnmount(() => {
    dispatch(knmListSetFindData([]));
  });

  const handleTextInputClear = useCallback(() => {
    setIsSearchTextSelected(false);
    formRef?.current?.change('ervkKnoOrganizationTitle');
    formRef?.current?.change('ervkKnoOrganization');
  }, []);

  const handleTextInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const tagName = (e?.relatedTarget as Element)?.tagName;

      if (tagName !== 'UL') {
        debouncedFindErvkOrganisations('');
        setOrganisationAutocompleteOption(undefined);
      }
    },
    [debouncedFindErvkOrganisations],
  );

  return (
    <div className="shared-row-field">
      <AutoComplete
        options={organisationAutocompleteOption}
        noFoundMessage="КНО соответствующие тексту поиска не найдены"
        handleAutoCompleteSelect={handleAutoCompleteSelect}
      >
        <TextInputField
          id="ervkKnoOrganizationTitle"
          name="ervkKnoOrganizationTitle"
          placeholder="Введите ОГРН или часть наименования КНО"
          clearable={isSearchTextSelected}
          onChange={handleTextInputChange}
          onClear={handleTextInputClear}
          onBlur={handleTextInputBlur}
          validate={makeRequired(REQUIRED_ERROR)}
        />
        <FormSpy subscription={{ errors: true }}>
          {props => {
            const fieldError = props.errors?.ervkKnoOrganizationTitle;

            let errorMessage: string | undefined =
              !fieldError && !isSearchTextSelected ? 'Не выбран Контрольный надзорный орган' : undefined;

            const notification = notifications?.find(item => item.messageType === 'GET_KNO_SUPERVISION');

            if (!errorMessage && notification && isSearchTextSelected) {
              errorMessage = notification.renderContent?.toString();
            }

            if (errorMessage) {
              return <p className="shared-input-error shared-row__small-left-margin">{errorMessage}</p>;
            }

            return null;
          }}
        </FormSpy>
      </AutoComplete>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IErvkKnoOrganisationSearchStoreProps => ({
  ervkKnoOrganisations: getKnoErvkOrganisations(state),
  notifications: getNotices(state),
});

export const ErvkKnoOrganisationSearch = connect(mapStateToProps)(ErvkKnoOrganisationSearchWithoutStore);
